import { Field, FieldProps } from 'formik';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Grid, Theme } from '@mui/material';
import { PoolTextField, PoolTypography } from '@synergy/components';
import { createStyles, makeStyles } from '@mui/styles';
import { getCurrencySymbol } from '@synergy/utils';

import { Offer } from '../../../common/models/offer';
import { useSelector } from '../../../store/use-appstate-selector';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            paddingRight: 18,
        },
        innerContainer: {
            width: 350,
        },
        delivery: {
            '& > div': {
                height: 56,
            },
            '& > div > input': {
                fontSize: 15,
                textAlign: 'right',
                paddingRight: 16,
            },
        },
        summaryLeft: {
            padding: theme.spacing(0, 3),
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            width: 210,
        },
        summaryRight: {
            padding: '12px 0',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            minHeight: theme.spacing(7),
            width: 105,
        },
        boxesLeft: {
            paddingRight: theme.spacing(1),
        },
        boxesRight: {
            paddingLeft: theme.spacing(1),
        },
        totalSumHighlight: {
            backgroundColor: theme.general.colors.activitySidebarBackground,
        },
        padRight: {
            fontSize: 15,
            marginRight: 16,
        },
        text: {
            fontSize: 15,
        },
        vatContainer: {
            marginTop: 17,
            marginRight: 16,
            color: theme.palette.text.disabled,
            textAlign: 'right',
        },
        vatText: {
            fontSize: 11,
        },
    })
);

interface OffersTableFooterProps {
    currency: string;
    offer: Offer;
    itemIndex: number;
    readonly: boolean;
    selectedOfferItems: number[];
}

export const OffersTableFooter = (props: OffersTableFooterProps) => {
    // hooks
    const classes = useStyles();

    // store
    const locale = useSelector(state => state.locale);

    const { currency, itemIndex, readonly } = props;

    const prefix = `items.${itemIndex}.offer.`;

    const total =
        props.offer.offerItems.reduce(
            (sum, offerItem) =>
                props.selectedOfferItems.includes(offerItem.id ?? -1)
                    ? sum + (offerItem.price || 0) * offerItem.quantity
                    : sum,
            0
        ) +
        (props.offer.deliveryPrice || 0) +
        (props.offer.surcharge || 0);

    return (
        <div className={classes.container}>
            <Grid container={true} spacing={4} direction="column" alignItems="flex-end">
                <Grid item={true} container={true} direction="row" className={classes.innerContainer}>
                    {/* delivery costs */}
                    <Grid item={true} xs={8} className={classes.summaryLeft}>
                        <PoolTypography id="app.label.deliverCosts" className={classes.text} />
                    </Grid>
                    <Grid item={true} xs={4} className={classes.summaryRight}>
                        <Field name={`${prefix}deliveryPrice`}>
                            {({ field, meta }: FieldProps<Offer>) => (
                                <PoolTextField
                                    fieldType="dense"
                                    className={classes.delivery}
                                    error={meta.touched && !!meta.error}
                                    helperText={
                                        meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                    }
                                    readOnly={readonly}
                                    {...field}
                                    value={field.value}
                                    isNumeric={true}
                                    decimalScale={4}
                                    placeholder={(0.0).toLocaleString(locale.replace('_', '-'), {
                                        minimumFractionDigits: 2,
                                    })}
                                />
                            )}
                        </Field>
                    </Grid>

                    {/* surcharge */}
                    <Grid item={true} xs={8} className={classes.summaryLeft}>
                        <PoolTypography id="app.label.surcharge" className={classes.text} />
                    </Grid>
                    <Grid item={true} xs={4} className={classes.summaryRight}>
                        <Field name={`${prefix}surcharge`}>
                            {({ field, meta }: FieldProps<Offer>) => (
                                <PoolTextField
                                    fieldType="dense"
                                    className={classes.delivery}
                                    error={meta.touched && !!meta.error}
                                    helperText={
                                        meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                    }
                                    readOnly={readonly}
                                    {...field}
                                    value={field.value}
                                    decimalScale={4}
                                    isNumeric={true}
                                    placeholder={(0.0).toLocaleString(locale.replace('_', '-'), {
                                        minimumFractionDigits: 2,
                                    })}
                                />
                            )}
                        </Field>
                    </Grid>

                    {/* net total */}
                    <Grid item={true} xs={8} className={`${classes.summaryLeft} ${classes.totalSumHighlight}`}>
                        <PoolTypography id="app.label.subtotal" className={classes.text} />
                    </Grid>
                    <Grid item={true} xs={4} className={`${classes.summaryRight} ${classes.totalSumHighlight}`}>
                        <PoolTypography className={classes.padRight}>
                            {getCurrencySymbol(currency, 'de')}&nbsp;
                            <FormattedNumber value={total} minimumFractionDigits={2} maximumFractionDigits={4} />
                        </PoolTypography>
                    </Grid>

                    <Grid item={true} xs={12} className={classes.vatContainer}>
                        <PoolTypography className={classes.vatText}>
                            <FormattedMessage id="app.offers.vatText" values={{ value: currency }} />
                        </PoolTypography>
                    </Grid>
                </Grid>
            </Grid>
        </div>
    );
};
